import { memo } from "react";

import { Badge, Card, Group, Stack, Text } from "@mantine/core";
import { TbTopologyStarRing3 } from "react-icons/tb";

import { formatDate } from "modules/core/utils/helpers";
import {
  insightConfidenceLevels,
  insightImpactLevels,
  insightTypes
} from "modules/insights/components/InsightCard/constants";
import { insightsPrivateRoutesConstants } from "modules/insights/routes/private/routesConstants";

import { InsightType } from "modules/insights/types/entities";

export type DisplayInsightProps = {
  id: number;
  experiment_id: string;
  project_id: string;
  created_at: string;
  name: string;
  description?: string;
  impact_level?: "low" | "medium" | "high";
  confidence_level?: "low" | "medium" | "high";
  insight_type: InsightType;
};

const DisplayInsight = (props: DisplayInsightProps) => {
  const {
    id,
    project_id,
    created_at,
    name,
    description,
    impact_level,
    confidence_level,
    insight_type
  } = props;

  return (
    <Card
      withBorder
      shadow="xs"
      component="a"
      href={insightsPrivateRoutesConstants.single_insight.href(project_id, id)}
      target="_blank"
      rel="noopener noreferrer"
      radius="lg"
    >
      <Stack gap="xs">
        <Badge
          size="xs"
          color="blue"
          variant="transparent"
          tt="capitalize"
          leftSection={<TbTopologyStarRing3 size={12} />}
        >
          Insight
        </Badge>

        <div>
          <Text size="sm" fw={600}>
            {name}
          </Text>

          {description ? (
            <Text size="xs" c="gray.6">
              {description}
            </Text>
          ) : null}
        </div>

        <Group justify="space-between">
          <Group gap="xs">
            {impact_level ? (
              <Badge
                color={insightImpactLevels[impact_level].color}
                size="xs"
                tt="capitalize"
                variant="light"
              >
                {insightImpactLevels[impact_level].label}
              </Badge>
            ) : null}

            {confidence_level ? (
              <Badge
                color={insightConfidenceLevels[confidence_level].color}
                size="xs"
                tt="capitalize"
                variant="light"
              >
                {insightConfidenceLevels[confidence_level].label}
              </Badge>
            ) : null}

            {insight_type ? (
              <Badge variant="light" tt="capitalize" color="teal" size="xs">
                {insightTypes[insight_type]?.label || insight_type}
              </Badge>
            ) : null}
          </Group>

          <Text size="xs" c="gray.6">
            Created:{" "}
            {formatDate({
              datestring: created_at,
              dateFormat: "dd MMM yyyy"
            })}
          </Text>
        </Group>
      </Stack>
    </Card>
  );
};

export default memo(DisplayInsight);
